import { debounce, useRouter, useRoute } from '@pet/base.utils';
import { invoke } from '@yoda/bridge';
import cookie from 'js-cookie';

export type RouterUrl = `/${string}`;
export type HttpUrl = `http${string}`;
export type KwaiUrl = `kwai://${string}` | `ksnebula://${string}`;

export type JumpUrl = RouterUrl | HttpUrl | KwaiUrl;

export interface OpenOptions {
    /** 需要携带的参数 */
    query?: Record<string, string | number>;
    /** 保留当前 query， kwai 协议此项无效 */
    keepQuery?: boolean;
    /** 强制新开 webview 打开 */
    forceOpenInNewWebview?: boolean;
}

const KUAISHOU_KPN = 'KUAISHOU';

const setQueryParams = (
    searchParams: URL['searchParams'],
    query: Record<string, string | number>,
    ifNullCover?: boolean,
) => {
    Object.keys(query).forEach((key) => {
        const value = typeof query[key] === 'number' ? String(query[key]) : String(query[key] ?? '');
        /** 默认全覆盖 */
        if (!ifNullCover) {
            searchParams.set(key, value);
        }

        /** 之前没有才覆盖 */
        if (ifNullCover && !searchParams.get(key)) {
            searchParams.set(key, value);
        }
    });
};

function openUrlInIframe(link: string) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', link);
    iframe.setAttribute('style', 'display:none');
    document.body.appendChild(iframe);
    setTimeout(() => document.body.removeChild(iframe), 100);
}

const launch = (url: string) => {
    const kpn = cookie.get('kpn') ?? KUAISHOU_KPN;
    const app = kpn === KUAISHOU_KPN ? 'kwai' : 'ksnebula';
    const urlWithoutProtocol = url.replace(/^kwai:\/\//u, '').replace(/^ksnebula:\/\//u, '');
    openUrlInIframe(app + '://' + urlWithoutProtocol);
};

/**
 *
 * @param base 路由的根路径, 默认使用 vue route 的 base
 */
export function useOpenPage(base = '') {
    const router = useRouter();
    const route = useRoute();
    // @ts-expect-error
    const routeBase = (router.options.base ?? base).replace(/\/$/, '') as string;
    async function openPage(url: JumpUrl | string, options: OpenOptions = {}) {
        const { keepQuery = true, forceOpenInNewWebview = false, query = {} } = options;
        const isKwai = url.startsWith('kwai') || url.startsWith('ksnebula');
        const isPath = url.startsWith('/');
        const isHttp = url.startsWith('http');
        const shouldKeepQuery = keepQuery && !isKwai;

        const currentRouteQuery = { ...route.query };
        if (isHttp && keepQuery) {
            // layoutType 不能保留
            delete currentRouteQuery.layoutType;
        }

        const routeQuery = shouldKeepQuery
            ? {
                  ...currentRouteQuery,
                  ...query,
              }
            : {
                  ...query,
              };
        // 强制删掉 inActivityWeb 和 router.push标记
        delete routeQuery.inActivityWeb;
        delete routeQuery.useRouterPush;

        const openInNewWebview = forceOpenInNewWebview;
        if (isPath && !openInNewWebview) {
            router.push({
                path: url,
                query: routeQuery as any,
            });
            return;
        }

        const fullURL = new URL(isPath ? `${routeBase}${url}` : url, window.location.origin);
        setQueryParams(fullURL.searchParams, routeQuery as Record<string, string>, true);

        if (isKwai) {
            launch(fullURL.href);
            return;
        }

        if (forceOpenInNewWebview) {
            await invoke('tool.loadUrlOnNewPage', { url: fullURL.href, type: 'back' }, { timeout: 1000 }).catch(
                () => false,
            );
        } else {
            window.location.href = fullURL.href;
        }
    }

    return debounce(openPage, 200, true);
}
